import type { JourneyManifest } from './journeyManifest.schema';

/**
 * Pluggable texture loader so the preloader works without Phaser in tests.
 * In production, the implementation wraps Phaser's LoaderPlugin + TextureManager.
 */
export interface ZoneTextureLoader {
  loadZoneTexture(sceneKey: string): Promise<void>;
  unloadZoneTexture(sceneKey: string): void;
  isTextureLoaded(sceneKey: string): boolean;
}

const DEFAULT_BUDGET = 5;

export class ZonePreloader {
  private readonly manifest: JourneyManifest;
  private readonly loader: ZoneTextureLoader;
  private readonly budget: number;

  /** Most-recently-visited at the end. */
  private readonly lruOrder: string[] = [];
  private readonly loading = new Set<string>();

  /**
   * Optional server-supplied connection graph (SMA-42 Phase 2). When set,
   * `preloadNeighbors` uses this — covering all transition kinds, not just
   * `edge-pan` — so the client can warm any zone the server will accept a
   * `MSG_ZONE_TRANSITION_REQUEST` for. Falls back to the manifest's
   * `edge-pan`-only walk when not set, preserving the original AGE-1419
   * preloader semantics.
   */
  private connectionGraph: ReadonlyMap<string, readonly string[]> | null = null;

  constructor(
    manifest: JourneyManifest,
    loader: ZoneTextureLoader,
    budget = DEFAULT_BUDGET,
  ) {
    this.manifest = manifest;
    this.loader = loader;
    this.budget = budget;
  }

  /**
   * Return scene keys reachable from `sceneKey` via `edge-pan` edge zones.
   * Only direct neighbors (one hop) — not transitive.
   */
  getEdgePanNeighbors(sceneKey: string): string[] {
    const spec = this.manifest.scenes.find(s => s.key === sceneKey);
    if (!spec) return [];
    const neighbors = new Set<string>();
    for (const ez of spec.edgeZones) {
      if (ez.transitionKind === 'edge-pan') {
        neighbors.add(ez.targetScene);
      }
    }
    return [...neighbors];
  }

  /**
   * SMA-42 Phase 2 — install (or replace) the server-supplied connection
   * graph. Pass `null` to revert to manifest-driven `edge-pan`-only
   * preloading.
   */
  setConnectionGraph(edges: Record<string, readonly string[]> | null): void {
    if (edges === null) {
      this.connectionGraph = null;
      return;
    }
    const map = new Map<string, readonly string[]>();
    for (const [zoneId, neighbors] of Object.entries(edges)) {
      map.set(zoneId, [...neighbors]);
    }
    this.connectionGraph = map;
  }

  /**
   * Resolve the neighbor set used by `preloadNeighbors`. Prefers the
   * server graph when present so painted scenes connected by `edge-crossfade`
   * or `doorway` get warmed too — not just `edge-pan` pairs.
   */
  getNeighbors(sceneKey: string): string[] {
    if (this.connectionGraph) {
      const fromGraph = this.connectionGraph.get(sceneKey);
      if (fromGraph) return [...fromGraph];
    }
    return this.getEdgePanNeighbors(sceneKey);
  }

  isLoaded(sceneKey: string): boolean {
    return this.loader.isTextureLoaded(sceneKey);
  }

  /**
   * Touch `sceneKey` in the LRU, preload its neighbors (server graph when
   * present, else manifest `edge-pan` neighbors), and evict zones that
   * exceed the budget.
   */
  async preloadNeighbors(currentSceneKey: string): Promise<void> {
    this.touch(currentSceneKey);

    const neighbors = this.getNeighbors(currentSceneKey);
    if (neighbors.length === 0) return;

    const toLoad = neighbors.filter(
      k => !this.loader.isTextureLoaded(k) && !this.loading.has(k),
    );

    const promises = toLoad.map(async key => {
      this.loading.add(key);
      try {
        await this.loader.loadZoneTexture(key);
        this.touchIfAbsent(key);
      } finally {
        this.loading.delete(key);
      }
    });

    await Promise.all(promises);
    this.evict(currentSceneKey);
  }

  /** Mark a zone as recently visited (call on scene enter). */
  touch(sceneKey: string): void {
    const idx = this.lruOrder.indexOf(sceneKey);
    if (idx !== -1) this.lruOrder.splice(idx, 1);
    this.lruOrder.push(sceneKey);
  }

  /** Add to LRU only if not already tracked (for freshly-loaded neighbors). */
  private touchIfAbsent(sceneKey: string): void {
    if (!this.lruOrder.includes(sceneKey)) {
      this.lruOrder.push(sceneKey);
    }
  }

  /**
   * Evict least-recently-visited zones that exceed the budget.
   * Never evicts `protectedKey` (the current scene).
   */
  private evict(protectedKey: string): void {
    while (this.lruOrder.length > this.budget) {
      const victim = this.lruOrder.find(k => k !== protectedKey);
      if (!victim) break;
      const idx = this.lruOrder.indexOf(victim);
      this.lruOrder.splice(idx, 1);
      if (this.loader.isTextureLoaded(victim)) {
        this.loader.unloadZoneTexture(victim);
      }
    }
  }

  /** Snapshot of current LRU order (oldest first). Exposed for tests. */
  getLruSnapshot(): readonly string[] {
    return [...this.lruOrder];
  }
}
